/**
 * Offices — where attendance may be taken, and who each office applies to.
 * Route: /locations
 * @file src/web/features/locations/pages/index.tsx
 *
 * @llm-rule WHEN: A Company Admin or Head sets up the places people clock in from
 * @llm-rule NOTE: Three ways in, because a Company Admin has whichever one to hand:
 *   search by name, paste a Google Maps link, or stand there and use the
 *   current position. All three land on the same confirm step.
 * @llm-rule AVOID: Trusting the browser's idea of "inside". The fence verdict
 *   at clock-in is always recomputed on the server from raw coordinates.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Skeleton, Textarea, toast, useConfirm } from '@bloomneo/uikit';
import {
  Building2,
  ChevronDown,
  Crosshair,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { AuthGuard } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel, PersonAvatar, SectionLabel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { useGeolocation } from '../../../shared/hooks/useGeolocation';
import { useAuth } from '../../auth';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';
import { MapPreview } from '../components/MapPreview';

interface Office {
  id: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  radiusM: number;
  appliesToRoles: string[];
  note: string | null;
  isActive: boolean;
}

interface Person {
  id: string;
  name: string | null;
  orgRole: OrgRole;
  title: string | null;
  homeLocationId: string | null;
}

interface Place {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

/** Ranks an office can be pinned to. Empty selection means everyone. */
const ROLES: OrgRole[] = ['EMPLOYEE', 'MANAGER', 'HEAD', 'COMPANY_ADMIN'];

/** Radius presets, because "how many metres is my building?" is a hard question. */
const RADIUS_PRESETS = [
  { m: 50, label: 'One floor' },
  { m: 150, label: 'A building' },
  { m: 400, label: 'A campus' },
  { m: 1000, label: 'A large site' },
];

interface Draft {
  id: string | null;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  radiusM: number;
  roles: string[];
  note: string;
}

const EMPTY_DRAFT: Draft = {
  id: null,
  name: '',
  address: '',
  latitude: 0,
  longitude: 0,
  radiusM: 150,
  roles: [],
  note: '',
};

/** Metres between two coordinates — the same haversine the server uses. */
function distanceM(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }) {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
}

function rolesLabel(roles: string[]): string {
  if (!roles.length) return 'Everyone';
  return roles.map((r) => ROLE_LABEL[r as OrgRole] ?? r).join(', ');
}

/* -------------------------------------------------------------------------- */
/* Office row — MODULE level, so editing one never remounts the list          */
/* -------------------------------------------------------------------------- */

function OfficeCard({
  office,
  postedCount,
  myFix,
  onEdit,
  onRetire,
  canManage,
}: {
  office: Office;
  /** How many people are pinned here, so the card says who it covers. */
  postedCount: number;
  myFix: { latitude: number; longitude: number } | null;
  onEdit: (office: Office) => void;
  onRetire: (office: Office) => void;
  /** Company Admin only — Heads can assign, not create or edit. */
  canManage: boolean;
}) {
  const away = myFix ? distanceM(myFix, office) : null;
  const inside = away !== null && away <= office.radiusM;

  return (
    /*
      `min-w-0` is load-bearing.

      This card is a CSS grid item, and a grid track defaults to
      `min-width: auto` — so its widest child (a long office name, the
      coordinate line under the map) stretched the track past the phone's
      viewport and the whole page scrolled sideways. `truncate` on the name
      could never take effect while the parent was free to grow.
    */
    <Panel className="flex min-w-0 flex-col gap-3.5">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-start gap-2">
            <Building2 className="mt-0.5 size-4 flex-none self-start text-ink-faint" strokeWidth={1.8} />
            {/* Wraps rather than truncates: with two action buttons beside it
                there is room for ~20 characters on a phone, and "Acme HQ —
                Gachibowli Tech Park" clipped to "Acme HQ — Gachibowli…" hides
                the part that distinguishes one site from another. */}
            <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-tight text-ink">
              {office.name}
            </h3>
          </div>
          {office.address && (
            <p className="mt-1 line-clamp-2 text-[12.5px] leading-relaxed text-ink-soft">
              {office.address}
            </p>
          )}
        </div>

        {/* Only the Company Admin creates, edits or retires an office. A Head
            still assigns people to one — that lives on the person, below. */}
        {canManage && (
          <div className="flex flex-none gap-1">
            <Button
              size="sm"
              variant="outline"
              className="size-10 rounded-xl p-0"
              onClick={() => onEdit(office)}
              aria-label={`Edit ${office.name}`}
            >
              <Pencil className="size-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="size-10 rounded-xl p-0 text-flag"
              onClick={() => onRetire(office)}
              aria-label={`Retire ${office.name}`}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        )}
      </div>

      <MapPreview
        latitude={office.latitude}
        longitude={office.longitude}
        radiusM={office.radiusM}
        label={office.name}
        height={160}
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-sunk px-2.5 py-1 text-[11.5px] text-ink-soft">
          <Users className="size-3.5" strokeWidth={1.8} />
          {rolesLabel(office.appliesToRoles)}
        </span>
        <span className="tnum rounded-full bg-paper-sunk px-2.5 py-1 text-[11.5px] text-ink-soft">
          {office.radiusM} m fence
        </span>

        {/* A posting overrides the role rule, so an office covering "Everyone"
            with three people pinned to it is worth saying out loud. */}
        {postedCount > 0 && (
          <span
            className="tnum rounded-full px-2.5 py-1 text-[11.5px] font-medium"
            style={{ background: 'var(--state-ok-bg)', color: 'var(--dot-appreciation)' }}
          >
            {postedCount} pinned here
          </span>
        )}

        {/* Where the viewer is standing relative to this fence, right now. */}
        {away !== null && (
          <span
            className="tnum rounded-full px-2.5 py-1 text-[11.5px] font-medium"
            style={{
              background: inside ? 'var(--state-ok-bg)' : 'var(--state-warn-bg)',
              color: inside ? 'var(--dot-appreciation)' : 'var(--dot-offsite)',
            }}
          >
            {inside ? `You are inside · ${away} m` : `You are ${away} m away`}
          </span>
        )}
      </div>

      {office.note && (
        <p className="rounded-lg bg-paper-sunk px-3 py-2 text-[12px] leading-relaxed text-ink-soft">
          {office.note}
        </p>
      )}
    </Panel>
  );
}


/* -------------------------------------------------------------------------- */
/* Postings — people pinned to one office, which overrides the role rule      */
/* -------------------------------------------------------------------------- */

function PostingPanel({
  offices,
  people,
  busyId,
  onAssign,
}: {
  offices: Office[];
  people: Person[];
  busyId: string | null;
  onAssign: (person: Person, locationId: string) => void;
}) {
  const [query, setQuery] = useState('');

  if (people.length === 0) return null;

  const term = query.trim().toLowerCase();
  const shown = people.filter(
    (p) =>
      !term ||
      (p.name ?? '').toLowerCase().includes(term) ||
      (p.title ?? '').toLowerCase().includes(term),
  );

  const postedCount = people.filter((p) => p.homeLocationId).length;

  return (
    <Panel className="flex flex-col gap-3">
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionLabel>Who is posted where</SectionLabel>
          <span className="text-[11.5px] text-ink-faint">
            {postedCount} of {people.length} pinned
          </span>
        </div>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-soft">
          A posting pins one person to one office and <b className="font-semibold">overrides</b> the
          role rules above — use it for someone stationed at a client site. Leave it on “By role”
          and they follow whichever offices match their rank.
        </p>
      </div>

      {/* Worth having the moment a company is more than a handful of people. */}
      {people.length > 6 && (
        <div className="flex items-center gap-2 rounded-xl border border-rule bg-paper px-3 py-2">
          <Search className="size-4 flex-none text-ink-ghost" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-ghost"
          />
        </div>
      )}

      {shown.length === 0 ? (
        <p className="rounded-xl border border-dashed border-rule px-4 py-8 text-center text-[12.5px] text-ink-faint">
          Nobody matches “{query}”.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {shown.map((person) => {
            const posted = offices.find((o) => o.id === person.homeLocationId) ?? null;

            return (
              /*
                STACKED, not side by side.

                A native <select> sizes itself to its widest option, and an
                office called "Acme HQ — Gachibowli Tech Park" is wider than a
                phone. Sitting it beside the name pushed the row past the
                viewport: the page scrolled sideways and the names were off
                screen entirely. The picker now owns its own full-width line
                below the person, inside a `min-w-0` column so it can never
                widen its parent again.
              */
              <li
                key={person.id}
                className="flex flex-col gap-2 rounded-xl border border-rule bg-paper p-3"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <PersonAvatar person={person} size={32} />

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-medium text-ink">
                      {person.name}
                    </div>
                    <div className="truncate text-[11.5px] text-ink-faint">
                      {person.title || ROLE_LABEL[person.orgRole]}
                    </div>
                  </div>

                  {/* Where they stand today, before you touch the picker. */}
                  <span
                    className="flex-none rounded-full px-2 py-0.5 text-[10.5px] font-medium"
                    style={
                      posted
                        ? { background: 'var(--state-ok-bg)', color: 'var(--dot-appreciation)' }
                        : { background: 'var(--paper-sunk)', color: 'var(--ink-faint)' }
                    }
                  >
                    {posted ? 'Pinned' : 'By role'}
                  </span>
                </div>

                <div className="relative min-w-0">
                  <select
                    value={person.homeLocationId ?? ''}
                    disabled={busyId === person.id}
                    onChange={(e) => onAssign(person, e.target.value)}
                    aria-label={`Posting for ${person.name}`}
                    className="h-10 w-full min-w-0 max-w-full appearance-none truncate rounded-xl border border-rule bg-paper-sunk pl-3 pr-9 text-[12.5px] text-ink disabled:opacity-60"
                  >
                    <option value="">By role — follows their rank</option>
                    {offices.map((office) => (
                      <option key={office.id} value={office.id}>
                        {office.name}
                      </option>
                    ))}
                  </select>

                  {/* `appearance-none` removes the native arrow, so draw one. */}
                  <ChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-ink-faint"
                    aria-hidden
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */

function LocationsPage() {
  const { request } = useAppRequest();
  const { user } = useAuth();
  const confirm = useConfirm();
  const geo = useGeolocation(false);

  const [offices, setOffices] = useState<Office[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [postingBusy, setPostingBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [provider, setProvider] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [result, roster] = await Promise.all([
      request<{ locations: Office[] }>('/api/location'),
      request<{ people: Person[] }>('/api/org/people'),
    ]);

    if (result.ok) setOffices(result.data.locations);
    else toast.error(result.error);

    if (roster.ok) setPeople(roster.data.people);
    setLoading(false);
  }, [request]);

  /** Pin someone to one office, or send them back to the role rules. */
  async function assignPosting(person: Person, locationId: string) {
    setPostingBusy(person.id);
    const result = await request(`/api/org/people/${person.id}`, {
      method: 'PUT',
      body: { homeLocationId: locationId || null },
    });
    setPostingBusy(null);

    if (!result.ok) return toast.error(result.error);
    toast.success(
      locationId
        ? `${person.name} is posted to ${offices.find((o) => o.id === locationId)?.name ?? 'that office'}`
        : `${person.name} now follows the role rules`,
    );
    await load();
  }

  useEffect(() => {
    void load();
  }, [load]);

  async function search() {
    const q = query.trim();
    if (q.length < 3) return toast.error('Type at least three characters, or paste a Maps link');

    setSearching(true);
    setPlaces(null);

    const result = await request<{ places: Place[]; provider: string; note?: string }>(
      `/api/location/search?q=${encodeURIComponent(q)}`,
    );

    if (result.ok) {
      setPlaces(result.data.places);
      setProvider(result.data.provider);
      if (result.data.note) toast.info(result.data.note);
      if (!result.data.places.length) toast.error('Nothing found. Try the full address, or paste a Maps link.');
    } else {
      toast.error(result.error);
    }
    setSearching(false);
  }

  /** Stand in the doorway and press the button. */
  async function useCurrentPosition() {
    const fix = await geo.locate();
    if (!fix) return toast.error(geo.error ?? 'Could not read your location');

    setDraft({
      ...EMPTY_DRAFT,
      name: '',
      address: `${fix.latitude.toFixed(6)}, ${fix.longitude.toFixed(6)}`,
      latitude: fix.latitude,
      longitude: fix.longitude,
      // A GPS fix is only good to its own accuracy, so never fence tighter
      // than the reading itself — that would flag people standing on the spot.
      radiusM: Math.max(100, Math.round((fix.accuracyM ?? 50) * 2)),
    });
    setPlaces(null);
    toast.success(`Location read to ±${Math.round(fix.accuracyM ?? 0)} m`);
  }

  function pick(place: Place) {
    setDraft({
      ...EMPTY_DRAFT,
      name: place.name.slice(0, 60),
      address: place.address,
      latitude: place.latitude,
      longitude: place.longitude,
    });
    setPlaces(null);
  }

  async function save() {
    if (!draft) return;
    if (!draft.name.trim()) return toast.error('Give this office a name');

    setSaving(true);
    const body = {
      name: draft.name.trim(),
      address: draft.address.trim() || null,
      latitude: draft.latitude,
      longitude: draft.longitude,
      radiusM: draft.radiusM,
      appliesToRoles: draft.roles,
      note: draft.note.trim() || null,
    };

    const result = draft.id
      ? await request(`/api/location/${draft.id}`, { method: 'PUT', body })
      : await request('/api/location', { method: 'POST', body });

    setSaving(false);
    if (!result.ok) return toast.error(result.error);

    toast.success(draft.id ? 'Office updated' : 'Office added');
    setDraft(null);
    setQuery('');
    await load();
  }

  async function retire(office: Office) {
    const ok = await confirm({
      title: `Retire ${office.name}?`,
      description:
        'Nobody will be able to clock in against this office. Attendance already taken here is kept.',
      confirmLabel: 'Retire',
    });
    if (!ok) return;

    const result = await request(`/api/location/${office.id}`, { method: 'DELETE' });
    if (!result.ok) return toast.error(result.error);
    toast.success(result.data ? (result.data as any).message : 'Office retired');
    await load();
  }

  const role = (user?.orgRole as OrgRole) ?? 'EMPLOYEE';
  // Offices are created, edited and retired by the Company Admin alone; a Head
  // opens this page to ASSIGN people to an existing office.
  const canManage = role === 'COMPANY_ADMIN';
  if (role !== 'COMPANY_ADMIN' && role !== 'HEAD') {
    return (
      <AppShell wide title="Offices">
        <Panel>
          <p className="text-[13px] text-ink-soft">
            Only the Company Admin or a Head can set the places attendance is taken from.
          </p>
        </Panel>
      </AppShell>
    );
  }

  return (
    <AppShell
      wide
      title="Offices"
      subtitle="The places attendance may be taken from. A clock-in inside one of these circles counts as on-site; anywhere else is flagged and needs a reason."
    >
      <div className="flex flex-col gap-5">
        {/* ---------------------------- Find a place ---------------------------- */}
        {!draft && (
          <Panel className="flex flex-col gap-3.5">
            <SectionLabel>Add an office</SectionLabel>

            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-ghost" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void search()}
                  placeholder="Search a place, or paste a Google Maps link"
                  className="h-11 rounded-xl pl-9 text-[13.5px]"
                />
              </div>

              <Button
                onClick={() => void search()}
                disabled={searching}
                className="btn-brand h-11 px-5 sm:w-auto"
              >
                {searching ? <Loader2 className="size-4 animate-spin" /> : 'Search'}
              </Button>

              <Button
                variant="outline"
                onClick={() => void useCurrentPosition()}
                disabled={geo.state === 'locating'}
                className="h-11 gap-2 rounded-xl px-4 text-[13px]"
              >
                {geo.state === 'locating' ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Crosshair className="size-4" strokeWidth={1.9} />
                )}
                I’m standing here
              </Button>
            </div>

            <p className="text-[11.5px] leading-relaxed text-ink-faint">
              Paste the full URL from the Maps address bar — short{' '}
              <code className="rounded bg-paper-sunk px-1">maps.app.goo.gl</code> links carry no
              coordinates. A bare <code className="rounded bg-paper-sunk px-1">17.44, 78.34</code>{' '}
              works too.
            </p>

            {places && places.length > 0 && (
              <ul className="flex flex-col divide-y divide-rule overflow-hidden rounded-xl border border-rule">
                {places.map((place, i) => (
                  <li key={`${place.latitude},${place.longitude},${i}`}>
                    <button
                      type="button"
                      onClick={() => pick(place)}
                      className="flex w-full items-start gap-3 px-3.5 py-3 text-left transition-colors hover:bg-paper-warm"
                    >
                      <MapPin className="mt-0.5 size-4 flex-none text-brand" strokeWidth={1.9} />
                      <span className="min-w-0">
                        <span className="block truncate text-[13px] font-medium text-ink">
                          {place.name}
                        </span>
                        <span className="block truncate text-[11.5px] text-ink-faint">
                          {place.address}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {provider === 'osm' && places && places.length > 0 && (
              <p className="text-[11px] text-ink-ghost">
                Results from OpenStreetMap. Set GOOGLE_MAPS_API_KEY on the server for Google’s
                place database.
              </p>
            )}
          </Panel>
        )}

        {/* ------------------------------ Confirm ------------------------------ */}
        {draft && (
          <Panel className="flex flex-col gap-4">
            <SectionLabel>{draft.id ? 'Edit office' : 'Confirm the office'}</SectionLabel>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-3.5">
                <div>
                  <label htmlFor="office-name" className="mb-1.5 block text-[12px] font-medium text-ink-soft">
                    Office name
                  </label>
                  <Input
                    id="office-name"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Head office"
                    className="h-11 rounded-xl text-[13.5px]"
                  />
                </div>

                <div>
                  <label htmlFor="office-address" className="mb-1.5 block text-[12px] font-medium text-ink-soft">
                    Address
                  </label>
                  <Input
                    id="office-address"
                    value={draft.address}
                    onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                    className="h-11 rounded-xl text-[13.5px]"
                  />
                </div>

                {/* Radius */}
                <div>
                  <div className="mb-1.5 flex items-baseline justify-between">
                    <label htmlFor="office-radius" className="text-[12px] font-medium text-ink-soft">
                      How far from this point still counts
                    </label>
                    <span className="tnum text-[12px] font-semibold text-ink">{draft.radiusM} m</span>
                  </div>
                  <input
                    id="office-radius"
                    type="range"
                    min={25}
                    max={2000}
                    step={25}
                    value={draft.radiusM}
                    onChange={(e) => setDraft({ ...draft, radiusM: Number(e.target.value) })}
                    className="w-full accent-[var(--brand)]"
                  />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {RADIUS_PRESETS.map((preset) => (
                      <button
                        key={preset.m}
                        type="button"
                        onClick={() => setDraft({ ...draft, radiusM: preset.m })}
                        className={`rounded-full px-2.5 py-1 text-[11.5px] transition-colors ${
                          draft.radiusM === preset.m
                            ? 'bg-brand text-white'
                            : 'bg-paper-sunk text-ink-soft hover:bg-rule'
                        }`}
                      >
                        {preset.label} · {preset.m} m
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3.5">
                <MapPreview
                  latitude={draft.latitude}
                  longitude={draft.longitude}
                  radiusM={draft.radiusM}
                  label={draft.name || 'this office'}
                  height={188}
                />

                {/* Who this office applies to */}
                <div>
                  <div className="mb-1.5 text-[12px] font-medium text-ink-soft">
                    Who clocks in here
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {ROLES.map((r) => {
                      const on = draft.roles.includes(r);
                      return (
                        <button
                          key={r}
                          type="button"
                          onClick={() =>
                            setDraft({
                              ...draft,
                              roles: on ? draft.roles.filter((x) => x !== r) : [...draft.roles, r],
                            })
                          }
                          className={`rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors ${
                            on
                              ? 'bg-brand text-white'
                              : 'bg-paper-sunk text-ink-soft hover:bg-rule'
                          }`}
                        >
                          {ROLE_LABEL[r]}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1.5 text-[11.5px] text-ink-faint">
                    {draft.roles.length === 0
                      ? 'Nobody selected — this office applies to everyone in the company.'
                      : `Only ${rolesLabel(draft.roles)} are expected here.`}
                  </p>
                </div>

                <div>
                  <label htmlFor="office-note" className="mb-1.5 block text-[12px] font-medium text-ink-soft">
                    Note for whoever clocks in here <span className="text-ink-ghost">(optional)</span>
                  </label>
                  <Textarea
                    id="office-note"
                    value={draft.note}
                    onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                    rows={2}
                    placeholder="Use the rear gate after 7pm."
                    className="resize-none rounded-xl text-[12.5px]"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void save()} disabled={saving} className="btn-brand px-5">
                {saving ? <Loader2 className="size-4 animate-spin" /> : draft.id ? 'Save changes' : 'Add this office'}
              </Button>
              <Button variant="outline" onClick={() => setDraft(null)} className="rounded-xl px-4">
                Cancel
              </Button>
            </div>
          </Panel>
        )}

        {offices.length > 0 && !draft && (
          <PostingPanel
            offices={offices}
            people={people}
            busyId={postingBusy}
            onAssign={(person, locationId) => void assignPosting(person, locationId)}
          />
        )}

        {/* ------------------------------- The list ------------------------------ */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <SectionLabel>
              {offices.length} {offices.length === 1 ? 'office' : 'offices'}
            </SectionLabel>

            {canManage && !draft && offices.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 rounded-full px-3 text-[12px]"
                onClick={() => void useCurrentPosition()}
              >
                <Plus className="size-3.5" />
                Add where I am
              </Button>
            )}
          </div>

          {loading ? (
            <Skeleton className="h-56 w-full rounded-[18px]" />
          ) : offices.length === 0 ? (
            <Panel>
              <div className="flex flex-col items-center gap-2 py-8 text-center">
                <MapPin className="size-7 text-ink-ghost" strokeWidth={1.5} />
                <p className="text-[13.5px] font-medium text-ink">No offices yet</p>
                <p className="max-w-[46ch] text-[12.5px] leading-relaxed text-ink-soft">
                  Until one is set, every clock-in counts as off-site and needs a reason. Search for
                  your building above, or stand at the door and press “I’m standing here”.
                </p>
              </div>
            </Panel>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {offices.map((office) => (
                <OfficeCard
                  key={office.id}
                  office={office}
                  postedCount={people.filter((p) => p.homeLocationId === office.id).length}
                  myFix={geo.fix}
                  canManage={canManage}
                  onEdit={(o) =>
                    setDraft({
                      id: o.id,
                      name: o.name,
                      address: o.address ?? '',
                      latitude: o.latitude,
                      longitude: o.longitude,
                      radiusM: o.radiusM,
                      roles: o.appliesToRoles ?? [],
                      note: o.note ?? '',
                    })
                  }
                  onRetire={(o) => void retire(o)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

export default function LocationsRoute() {
  return (
    <AuthGuard>
      <LocationsPage />
    </AuthGuard>
  );
}
